/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { func, object, string } from 'prop-types';
import { format, isSameMonth, isSameWeek } from 'date-fns';

import { Button, Grid, makeStyles } from '@material-ui/core';

import { convertDateStringToObj } from '../../helpers/Calendar-Helper';
import { calendarColorStyle } from '../../../Helpers/Style-Helper';
import { convertDateObjToDateStr } from '../../../DeliveryCalendarContainer/Partials/CalendarView/helpers/calendarUtility';

const useStyles = makeStyles(
    (theme) => ({
        root: {
            textAlign: 'center',
            justifyContent: 'flex-start',
            padding: '8px 38px 0',
            [theme.breakpoints.down(490)]: {
                padding: '8px 16px 0',
            },
        },
        rootToLeft: {
            justifyContent: 'flex-start',
        },
        headerCopy: {
            textAlign: 'center',
            fontSize: '0.875em',
            borderBottom: `1px solid ${calendarColorStyle.primary[100]}`,
            padding: '0 0 8px',
            margin: '0 0 16px',
        },
        monthlyRoot: {
            borderBottom: `1px solid ${calendarColorStyle.primary[100]}`,
            paddingBottom: '8px',
            marginBottom: '16px',
        },
        monthWrapper: {
            margin: '8px 0',
        },
        monthBtn: {
            backgroundColor: calendarColorStyle.common.white,
            margin: '0',
            width: '84px',
            height: '72px',
            border: `1px solid ${calendarColorStyle.primary[200]}`,
            color: calendarColorStyle.primary[600],
            textAlign: 'center',
            cursor: 'pointer',
            fontSize: '14px',
            lineHeight: '22px',
            fontWeight: '500',
            textTransform: 'none',
            padding: '6px 10px',
            '&>span': {
                fontSize: '14px',
            },
            '&:hover, &:visited, &:active, &:focus': {
                backgroundColor: calendarColorStyle.common.selection,
                border: `1px solid ${calendarColorStyle.primary[600]}`,
                color: calendarColorStyle.primary[600],
                fontWeight: '700',
            },
            [theme.breakpoints.down(626)]: {
                margin: '0 auto 6px',
            },
            [theme.breakpoints.down(500)]: {
                width: '78px',
                height: 'auto',
                padding: '4px',
                display: 'block',
            },
        },
        selectedMonthBtn: {
            backgroundColor: calendarColorStyle.common.selection,
            border: `1px solid ${calendarColorStyle.primary[600]}`,
            color: calendarColorStyle.primary[600],
            fontWeight: '700',
        },
    }),
    { name: 'MonthlyWeeklyCalendar' },
);

const MonthlyWeeklyCalendar = ({
    calendarData,
    calendarListByDates,
    handleSetSelectedDateDetails,
    selectedDate,
    setSelectedDate,
}) => {
    const classes = useStyles();
    const deliveryIndicator = calendarData?.deliveryIndicator; // WO, MO

    const handleSelectedDate = (date) => {
        const deliveryDate = new Date(date);
        const selDate = convertDateObjToDateStr(deliveryDate);

        const selDateProps = calendarListByDates[date];
        if (selDateProps) {
            setSelectedDate(selDate);
            handleSetSelectedDateDetails(selDateProps, date, true);
        }
    };

    const renderCalendar = () => {
        let selectedMonthDate = null;

        if (typeof selectedDate === 'string' && selectedDate !== '') {
            if (selectedDate.includes('-')) {
                selectedMonthDate = convertDateStringToObj(selectedDate);
            } else {
                selectedMonthDate = new Date(selectedDate);
            }
        } else {
            selectedMonthDate = selectedDate;
        }

        return calendarData.calendarDates.map((dateObj) => {
            const deliveryDate = new Date(dateObj.deliveryDate);
            const dayDate = format(deliveryDate, 'dd');
            const monthDate = format(deliveryDate, 'MMM');
            const yearDate = deliveryDate.getFullYear();

            // Check delivery date or range start date match with the month
            const isSelectedDate = (
                (deliveryIndicator === 'MO' && isSameMonth(deliveryDate, selectedMonthDate))
                || (deliveryIndicator === 'WO' && isSameWeek(deliveryDate, selectedMonthDate))
            );

            return (
                <Grid xs={3} spacing={0} key={`monthly-weekly-${dateObj.deliveryDate}`} id={`monthly-weekly-${dateObj.deliveryDate}`} className={classes.monthWrapper}>
                    <Button
                        id={`btn-monthly-weekly-${dateObj.deliveryDate}`}
                        data-test={`btn-monthly-weekly-${dateObj.deliveryDate}`}
                        className={isSelectedDate ? `${classes.monthBtn} ${classes.selectedMonthBtn}` : classes.monthBtn}
                        onClick={() => handleSelectedDate(dateObj.deliveryDate)}
                    >
                        {
                            deliveryIndicator === 'MO'
                                ? (
                                    <>{monthDate}.<br />{yearDate}</>
                                ) : (
                                    <>Week of {monthDate} {dayDate} {yearDate}</>
                                )
                        }
                    </Button>
                </Grid>
            );
        });
    };

    return (
        <Grid id="monthly-calendar" container className={classes.root}>
            <Grid xs={12} className={classes.headerCopy}>
                {'Due to natural harvests, a specific date isn\'t available.'}
                <br />
                Please choose a {deliveryIndicator === 'MO' ? 'month' : 'week'} below:
            </Grid>
            <Grid container className={classes.monthlyRoot}>
                {renderCalendar()}
            </Grid>
        </Grid>
    );
};

MonthlyWeeklyCalendar.propTypes = {
    calendarData: object.isRequired,
    calendarListByDates: object.isRequired,
    handleSetSelectedDateDetails: func.isRequired,
    selectedDate: string.isRequired,
    setSelectedDate: func.isRequired,
};

export default MonthlyWeeklyCalendar;
